import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Zap, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login, registerLeague } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "create"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [form, setForm] = useState({ display_name: "", email: "", password: "", league_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username.trim().toLowerCase(), password);
      navigate(user.role === "admin" || user.role === "owner" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await registerLeague({
        display_name: form.display_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        league_name: form.league_name.trim(),
      });
      navigate(user.role === "admin" || user.role === "owner" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not create league");
    } finally {
      setLoading(false);
    }
  };

  const field = (k) => ({ value: form[k], onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })) });

  return (
    <div className="min-h-screen bg-[#050505] grain flex items-center justify-center px-4">
      <div className="w-full max-w-md pp-fade-up">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-lg bg-[#013369] flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-extrabold tracking-tight">NFL Pick <span className="neon-text">House</span></span>
          </div>

          {mode === "login" ? (
            <>
              <h1 className="font-display text-2xl font-bold">Welcome back</h1>
              <p className="text-zinc-400 text-sm mt-1">Sign in with the credentials your commissioner gave you.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                  <label className="text-xs text-zinc-400 font-semibold">Username</label>
                  <input data-testid="login-username-input" required value={username}
                    onChange={(e) => setUsername(e.target.value)} autoCapitalize="none"
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A] focus:border-[#D50A0A] transition-colors" placeholder="username" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 font-semibold">Password</label>
                  <input data-testid="login-password-input" required type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A] focus:border-[#D50A0A] transition-colors" placeholder="••••••••" />
                </div>
                {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
                <button data-testid="login-submit-btn" disabled={loading}
                  className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold hover:shadow-[0_0_18px_rgba(213,10,10,0.4)] active:scale-95 transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
                </button>
              </form>
              <div className="mt-6 pt-5 border-t border-[#1F1F22] text-center">
                <p className="text-sm text-zinc-400">Want to run your own league?</p>
                <button data-testid="show-create-league-btn" onClick={() => { setError(""); setMode("create"); }}
                  className="mt-2 text-sm font-bold text-[#4C8DFF] hover:text-[#7ab0ff] transition-colors">
                  Create a League →
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold">Create your league</h1>
              <p className="text-zinc-400 text-sm mt-1">Start your own independent PickHouse league and become its commissioner.</p>
              <form onSubmit={submitCreate} className="mt-6 space-y-4" data-testid="create-league-form">
                <div>
                  <label className="text-xs text-zinc-400 font-semibold">Your name</label>
                  <input data-testid="cl-name-input" required {...field("display_name")}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#4C8DFF] focus:border-[#4C8DFF] transition-colors" placeholder="Jane Commissioner" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 font-semibold">Email</label>
                  <input data-testid="cl-email-input" required type="email" autoCapitalize="none" {...field("email")}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#4C8DFF] focus:border-[#4C8DFF] transition-colors" placeholder="you@example.com" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 font-semibold">Password</label>
                  <input data-testid="cl-password-input" required type="password" {...field("password")}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#4C8DFF] focus:border-[#4C8DFF] transition-colors" placeholder="At least 6 characters" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 font-semibold">League name</label>
                  <input data-testid="cl-league-input" required {...field("league_name")}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#4C8DFF] focus:border-[#4C8DFF] transition-colors" placeholder="The Sunday Funday League" />
                </div>
                {error && <p data-testid="create-league-error" className="text-sm text-red-400">{error}</p>}
                <button data-testid="create-league-submit-btn" disabled={loading}
                  className="w-full py-3 rounded-full bg-[#4C8DFF] text-white font-bold hover:shadow-[0_0_18px_rgba(76,141,255,0.4)] active:scale-95 transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create League &amp; Start
                </button>
              </form>
              <div className="mt-6 pt-5 border-t border-[#1F1F22] text-center">
                <button data-testid="back-to-login-btn" onClick={() => { setError(""); setMode("login"); }}
                  className="text-sm font-bold text-zinc-400 hover:text-white transition-colors">
                  ← Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
